import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Type,
  Minus,
  ChevronDown
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing your document...",
  className = "",
  minHeight = "min-h-[400px]"
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState('12');
  const [fontFamily, setFontFamily] = useState('Times New Roman');

  useEffect(() => {
    if (editorRef.current) {
      // Set initial content if provided
      if (value && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value.replace(/\n/g, '<br>');
      }
    }
  }, [value]);

  const handleCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      handleContentChange();
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      // Convert HTML back to plain text with line breaks
      const textContent = content
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/?(div|p)[^>]*>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
      onChange(textContent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle common keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          handleCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          handleCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          handleCommand('underline');
          break;
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const formattedText = text.replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, formattedText);
    handleContentChange();
  };

  const fontSizes = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];
  const fontFamilies = [
    'Times New Roman', 
    'Arial', 
    'Helvetica', 
    'Georgia', 
    'Verdana', 
    'Calibri',
    'Cambria',
    'Garamond'
  ];

  return (
    <div className={`border border-border rounded-lg bg-white shadow-sm ${className}`}>
      {/* Toolbar */}
      <div className="border-b border-border p-3 bg-gradient-to-r from-muted/30 to-muted/50">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Font Family */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="min-w-[120px] justify-between bg-white">
                <span className="truncate">{fontFamily}</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto bg-white border border-gray-200 shadow-lg z-50">
              {fontFamilies.map((font) => (
                <DropdownMenuItem 
                  key={font}
                  onClick={() => {
                    setFontFamily(font);
                    handleCommand('fontName', font);
                  }}
                  style={{ fontFamily: font }}
                  className="bg-white hover:bg-gray-100"
                >
                  {font}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Font Size */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="min-w-[60px] justify-between bg-white">
                <span>{fontSize}pt</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto bg-white border border-gray-200 shadow-lg z-50">
              {fontSizes.map((size) => (
                <DropdownMenuItem 
                  key={size}
                  onClick={() => {
                    setFontSize(size);
                    handleCommand('fontSize', '3');
                    if (editorRef.current) {
                      const selection = window.getSelection();
                      if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        const span = document.createElement('span');
                        span.style.fontSize = `${size}pt`;
                        try {
                          range.surroundContents(span);
                        } catch (e) {
                          span.appendChild(range.extractContents());
                          range.insertNode(span);
                        }
                      }
                    }
                  }}
                  className="bg-white hover:bg-gray-100"
                >
                  {size}pt
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6" />

          {/* Text Formatting */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('bold')}
            className="w-8 h-8 p-0"
          >
            <Bold className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('italic')}
            className="w-8 h-8 p-0"
          >
            <Italic className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('underline')}
            className="w-8 h-8 p-0"
          >
            <Underline className="w-4 h-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Text Alignment */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('justifyLeft')}
            className="w-8 h-8 p-0"
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('justifyCenter')}
            className="w-8 h-8 p-0"
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('justifyRight')}
            className="w-8 h-8 p-0"
          >
            <AlignRight className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('justifyFull')}
            className="w-8 h-8 p-0"
          >
            <AlignJustify className="w-4 h-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Lists */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('insertUnorderedList')}
            className="w-8 h-8 p-0"
          >
            <List className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('insertOrderedList')}
            className="w-8 h-8 p-0"
          >
            <ListOrdered className="w-4 h-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Insert Line */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCommand('insertHorizontalRule')}
            className="w-8 h-8 p-0"
          >
            <Minus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Document Editor - MS Word Style */}
      <div className="bg-gray-100 p-4 overflow-auto" style={{ height: 'calc(100vh - 200px)' }}>
        {/* Document container - like MS Word's page view */}
        <div className="max-w-[8.5in] mx-auto pb-8">
          <div 
            className="bg-white shadow-lg border border-gray-300 p-12 min-h-[11in] w-full max-w-[8.5in]"
            style={{
              minHeight: '11in',
              margin: '0 auto',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.1)'
            }}
          >
            <div
              ref={editorRef}
              contentEditable
              onInput={handleContentChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              className={`w-full outline-none focus:outline-none ${minHeight} text-black leading-relaxed`}
              style={{
                fontFamily: fontFamily,
                fontSize: `${fontSize}pt`,
                lineHeight: '1.15',
                minHeight: 'auto'
              }}
              data-placeholder={placeholder}
              suppressContentEditableWarning={true}
            />
          </div>
        </div>

        {/* Status bar like MS Word */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-200 border-t border-gray-300 px-4 py-1 text-xs text-gray-600 flex justify-between items-center z-10">
          <div className="flex items-center gap-4">
            <span>Page 1 of 1</span>
            <span>{value.split(/\s+/).filter(word => word.length > 0).length} words</span>
            <span>{value.length} characters</span>
          </div>
          <div className="flex items-center gap-2">
            <span>100%</span>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
          }
        `
      }} />
    </div>
  );
};